import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bot, Book, Home, Search, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useState } from 'react';

interface DocsLayoutProps {
  children: React.ReactNode;
}

const navigation = [
  {
    title: 'Getting Started',
    items: [
      { title: 'Introduction', href: '/docs/project-overview/introduction' },
      { title: 'Installation', href: '/docs/getting-started/installation' },
      { title: 'Creating First Chatbot', href: '/docs/getting-started/creating-first-chatbot' },
    ],
  },
  {
    title: 'Core Components',
    items: [
      { title: 'Chat Architecture', href: '/docs/chat-architecture' },
      { title: 'API Reference', href: '/docs/api-reference' },
      { title: 'Usage Examples', href: '/docs/usage-examples' },
    ],
  },
  {
    title: 'Integration Guides',
    items: [
      { title: 'Embedding Chat Widget', href: '/docs/integration-guides/embedding-chat-widget' },
      { title: 'Webhook Integration', href: '/docs/integration-guides/webhook-integration' },
      { title: 'PII Integration', href: '/docs/integration-guides/pii-integration' },
    ],
  },
  {
    title: 'Advanced Features',
    items: [
      { title: 'AI Response Validation', href: '/docs/advanced-features/ai-response-validation' },
      { title: 'PII Handling', href: '/docs/advanced-features/pii-handling' },
      { title: 'Streaming Responses', href: '/docs/advanced-features/streaming-responses' },
      { title: 'Webhook System', href: '/docs/advanced-features/webhook-system' },
    ],
  },
  {
    title: 'Customization',
    items: [
      { title: 'Theming System', href: '/docs/customization/theming-system' },
      { title: 'Rich Media', href: '/docs/customization/rich-media' },
    ],
  },
  {
    title: 'Technical Architecture',
    items: [
      { title: 'Frontend Architecture', href: '/docs/technical-architecture/frontend-architecture' },
      { title: 'Backend Services', href: '/docs/technical-architecture/backend-services' },
    ],
  },
];

function DocsNavigation() {
  const location = useLocation();

  return (
    <nav className="space-y-6">
      {navigation.map((section) => (
        <div key={section.title}>
          <h3 className="font-semibold text-sm text-gray-900 mb-3">
            {section.title}
          </h3>
          <ul className="space-y-2">
            {section.items.map((item) => (
              <li key={item.href}>
                <Link
                  to={item.href}
                  className={`block text-sm py-1 px-2 rounded-md transition-colors ${
                    location.pathname === item.href
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function DocsHeader() {
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="container flex h-14 items-center">
        <div className="mr-4 hidden md:flex">
          <Link to="/" className="mr-6 flex items-center space-x-2">
            <Bot className="h-6 w-6 text-primary" />
            <span className="font-bold">OfferChat</span>
          </Link>
          <nav className="flex items-center space-x-6 text-sm font-medium">
            <Link
              to="/docs"
              className="transition-colors hover:text-foreground/80 text-foreground/60"
            >
              Documentation
            </Link>
            <Link
              to="/portal"
              className="transition-colors hover:text-foreground/80 text-foreground/60"
            >
              Portal
            </Link>
          </nav>
        </div>
        
        {/* Mobile menu */}
        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              className="mr-2 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 md:hidden"
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle Menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="pr-0">
            <div className="px-7">
              <Link to="/" className="flex items-center space-x-2">
                <Bot className="h-6 w-6 text-primary" />
                <span className="font-bold">OfferChat</span>
              </Link>
            </div>
            <ScrollArea className="my-4 h-[calc(100vh-8rem)] px-6">
              <DocsNavigation />
            </ScrollArea>
          </SheetContent>
        </Sheet>

        <div className="flex flex-1 items-center justify-between space-x-2 md:justify-end">
          <div className="w-full flex-1 md:w-auto md:flex-none">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search documentation..."
                className="pl-8 md:w-[300px] lg:w-[400px]"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <nav className="flex items-center">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/">
                <Home className="h-4 w-4" />
                <span className="sr-only">Home</span>
              </Link>
            </Button>
          </nav>
        </div>
      </div>
    </header>
  );
}

export function DocsLayout({ children }: DocsLayoutProps) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <DocsHeader />
      <div className="container flex-1 items-start md:grid md:grid-cols-[220px_minmax(0,1fr)] md:gap-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-10">
        <aside className="fixed top-14 z-30 -ml-2 hidden h-[calc(100vh-3.5rem)] w-full shrink-0 md:sticky md:block">
          <ScrollArea className="h-full py-6 pr-6 lg:py-8">
            <DocsNavigation />
          </ScrollArea>
        </aside>
        <main className="relative py-6 lg:gap-10 lg:py-8 xl:grid xl:grid-cols-[1fr_300px]">
          <div className="mx-auto w-full min-w-0">
            <div className="prose prose-slate max-w-none dark:prose-invert">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
